import React from 'react';
import * as MuiCore from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import TimerIcon from '@material-ui/icons/Timer';
import PhoneIcon from '@material-ui/icons/Phone';

const StyledSlider = withStyles({
    root: {
        color: '#7F2062',
        height: 8,
        padding: '15px 0',
    },
    thumb: {
        height: 24,
        width: 24,
        backgroundColor: '#fff',
        border: '3px solid #7F2062',
        marginTop: -8,
        marginLeft: -12,
        boxShadow: '0 2px 6px rgba(127,32,98,0.35)',
        '&:focus, &:hover': {
            boxShadow: '0 0 0 8px rgba(127,32,98,0.15)',
        },
        '&$active': {
            boxShadow: '0 0 0 12px rgba(127,32,98,0.2)',
        },
    },
    active: {},
    track: {
        height: 8,
        borderRadius: 4,
        background: 'linear-gradient(90deg, #9F5789, #7F2062)',
    },
    rail: {
        height: 8,
        borderRadius: 4,
        backgroundColor: '#e0e0e0',
    },
    mark: {
        backgroundColor: 'transparent',
    },
    markLabel: {
        fontFamily: 'Sofia',
        fontSize: 11,
        color: '#999',
        marginTop: 4,
    },
    markLabelActive: {
        color: '#7F2062',
        fontWeight: 600,
    },
    valueLabel: {
        left: 'calc(-50% + 4px)',
        top: -22,
        '& *': {
            background: '#7F2062',
            color: '#fff',
            fontFamily: 'Sofia',
            borderRadius: 4,
        },
    },
})(MuiCore.Slider);

class BotDelayCard extends React.Component {
    constructor(props) {
        super(props);
        const currentSeconds = props.botDelay ? props.botDelay / 1000 : 0;
        this.state = {
            currentValue: currentSeconds,
            savedValue: currentSeconds
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.botDelay !== this.props.botDelay) {
            const currentSeconds = this.props.botDelay ? this.props.botDelay / 1000 : 0;
            this.setState({ savedValue: currentSeconds, currentValue: currentSeconds });
        }
    }

    handleSliderChange = (event, newValue) => {
        this.setState({ currentValue: newValue });
    };

    handleSaveClick = () => {
        this.props.onSave(this.state.currentValue * 1000);
    };

    handleCancelClick = () => {
        this.setState({ currentValue: this.state.savedValue });
    };

    hasChanges = () => this.state.currentValue !== this.state.savedValue;

    render() {
        const { classes, phoneNumbers } = this.props;
        const { currentValue } = this.state;
        const hasUnsavedChanges = this.hasChanges();
        const hasPhoneNumbers = phoneNumbers && phoneNumbers.length > 0;

        return (
            <MuiCore.Box className={classes.section}>
                <MuiCore.Box className={classes.sectionHeader}>
                    <MuiCore.Box className={classes.sectionTitle}>
                        <TimerIcon />
                        <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia-bold' }}>AI Response Delay</MuiCore.Typography>
                    </MuiCore.Box>
                    {hasPhoneNumbers && hasUnsavedChanges && (
                        <MuiCore.Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <MuiCore.Typography
                                variant="caption"
                                style={{ color: '#ff9800', fontFamily: 'Sofia', fontWeight: 600 }}
                            >
                                Unsaved Changes
                            </MuiCore.Typography>
                            <MuiCore.Button
                                size="small"
                                startIcon={<CloseIcon />}
                                onClick={this.handleCancelClick}
                                variant="outlined"
                                style={{ fontFamily: 'Sofia', textTransform: 'none', borderRadius: 100, padding: '4px 12px' }}
                            >
                                Reset
                            </MuiCore.Button>
                            <MuiCore.Button
                                size="small"
                                startIcon={<CheckIcon />}
                                onClick={this.handleSaveClick}
                                variant="contained"
                                style={{ fontFamily: 'Sofia', fontWeight: 700, textTransform: 'none', borderRadius: 100, padding: '4px 16px' }}
                            >
                                Save
                            </MuiCore.Button>
                        </MuiCore.Box>
                    )}
                </MuiCore.Box>

                <MuiCore.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, fontFamily: 'Sofia' }}>
                    Control how quickly your Grasshopper AI responds to callers
                </MuiCore.Typography>

                {!hasPhoneNumbers ? (
                    /* ── No phone number state ── */
                    <MuiCore.Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            padding: '20px 24px',
                            backgroundColor: '#fafafa',
                            border: '1px solid #cccdd2',
                            borderRadius: 12,
                            marginTop: 8,
                        }}
                    >
                        <MuiCore.Box
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                backgroundColor: '#f3f1ed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <PhoneIcon style={{ color: '#9e9e9e', fontSize: 22 }} />
                        </MuiCore.Box>
                        <MuiCore.Box>
                            <MuiCore.Typography
                                variant="body1"
                                style={{ fontFamily: 'Sofia', fontWeight: 600, color: '#333', marginBottom: 2 }}
                            >
                                No phone number allocated
                            </MuiCore.Typography>
                            <MuiCore.Typography
                                variant="body2"
                                style={{ fontFamily: 'Sofia', color: '#888' }}
                            >
                                Please add a phone number first to configure the AI response delay.
                            </MuiCore.Typography>
                        </MuiCore.Box>
                    </MuiCore.Box>
                ) : (
                    /* ── Slider state ── */
                    <MuiCore.Box style={{ padding: '8px 12px 24px 12px' }}>
                        {/* Current value badge */}
                        <MuiCore.Box style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                            <MuiCore.Box
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    backgroundColor: 'rgba(127,32,98,0.07)',
                                    border: '1px solid rgba(127,32,98,0.2)',
                                    borderRadius: 100,
                                    padding: '6px 16px',
                                }}
                            >
                                <TimerIcon style={{ fontSize: 18, color: '#7F2062' }} />
                                <MuiCore.Typography
                                    style={{ fontFamily: 'Sofia', fontWeight: 700, color: '#7F2062', fontSize: '1rem' }}
                                >
                                    {currentValue.toFixed(1)}s delay
                                </MuiCore.Typography>
                            </MuiCore.Box>
                            {currentValue === 0 && (
                                <MuiCore.Typography
                                    variant="caption"
                                    style={{ marginLeft: 12, fontFamily: 'Sofia', color: '#888' }}
                                >
                                    AI responds immediately
                                </MuiCore.Typography>
                            )}
                        </MuiCore.Box>

                        <StyledSlider
                            value={currentValue}
                            onChange={this.handleSliderChange}
                            min={0}
                            max={5}
                            step={0.5}
                            marks={[
                                { value: 0,   label: '0s' },
                                { value: 0.5, label: '0.5s' },
                                { value: 1,   label: '1s' },
                                { value: 1.5, label: '1.5s' },
                                { value: 2,   label: '2s' },
                                { value: 2.5, label: '2.5s' },
                                { value: 3,   label: '3s' },
                                { value: 3.5, label: '3.5s' },
                                { value: 4,   label: '4s' },
                                { value: 4.5, label: '4.5s' },
                                { value: 5,   label: '5s' },
                            ]}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${v}s`}
                        />
                    </MuiCore.Box>
                )}
            </MuiCore.Box>
        );
    }
}

export default BotDelayCard;
